import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { getProductTags, getProductCategoryKinds, getProductBrands, getProductAttributes } from '@/api/Products';
import { getBagsConfigurations, getOrFetchConfiguration, getPaymentMethods } from '@/utils/getConfiguration';

export const OFFERS_OPTIONS = [
  { id: 'withOffer', name: 'Εκπτωτικές' },
  { id: 'withBundleOffers', name: 'Συνδυαστικές' },
];

export default function useCategoryFilters(categoryId, options, initialFilters) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [brands, setBrands] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [categoryKinds, setCategoryKinds] = useState([]);
  const [isFirstTimeMounting, setIsFirstTimeMounting] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState(
    initialFilters || {
      tags: [],
      brands: [],
      attributes: [],
      categoryKinds: [],
      offers: [],
      sort: [],
    }
  );
  const [offerFiltering, setOfferFiltering] = useState('');

  useEffect(() => {
    if (!selectedFilters) return;

    if (selectedFilters.offers?.length) {
      if (selectedFilters.offers.length > 1) setOfferFiltering('withAnyOffer');
      else setOfferFiltering(selectedFilters.offers[0].id);
    } else {
      setOfferFiltering('');
    }
  }, [selectedFilters, setOfferFiltering]);

  const getCategoryFilters = async () => {
    if (!categoryId) return;
    setIsLoading(true);
    const configuration = await getOrFetchConfiguration();
    if (configuration && configuration?.catalog?.product?.filters) {
      configuration.catalog.product.filters.map(filter => {
        if (filter.filter === 'attributes') {
          options.allowAttributes = filter.enabled;
        }
        if (filter.filter === 'tags') {
          options.allowTags = filter.enabled;
        }
        if (filter.filter === 'brands') {
          options.allowBrands = filter.enabled;
        }
        if (filter.filter === 'category-kinds') {
          options.allowCategoryKinds = filter.enabled;
        }
      });
    }
    if (options?.allowTags) {
      try {
        const tagsRes = await getProductTags(categoryId, 'ALL_LEVELS', router.locale, undefined, offerFiltering);
        setTags(tagsRes);
      } catch (err) {
        console.error(err);
      }
    }

    if (options?.allowBrands) {
      try {
        const brandsRes = await getProductBrands(categoryId, 'ALL_LEVELS', router.locale, undefined, offerFiltering);
        setBrands(
          brandsRes.map(i => {
            //brands don't return an id and we need it for managing the state like in all other cases
            return { ...i, id: encodeURIComponent(i.name) };
          })
        );
      } catch (err) {
        console.error(err);
      }
    }

    if (options?.allowAttributes) {
      try {
        const attrsRes = await getProductAttributes(categoryId, 'ALL_LEVELS', router.locale, undefined, offerFiltering);
        setAttributes(attrsRes);
      } catch (err) {
        console.error(err);
      }
    }

    if (options?.allowCategoryKinds) {
      try {
        const categoryKindsRes = await getProductCategoryKinds(
          categoryId,
          'ALL_LEVELS',
          router.locale,
          undefined,
          offerFiltering
        );
        setCategoryKinds(
          categoryKindsRes.map(i => {
            //categoryKinds don't return an id and we need it for managing the state like in all other cases
            return { ...i, id: encodeURIComponent(i.name) };
          })
        );
      } catch (err) {
        console.error(err);
      }
    }
    setIsLoading(false);
  };

  const handleSelectFilter = (filterType, item) => {
    const existingSelection = selectedFilters?.[filterType]?.find?.(i => i.id === item.id);
    let newFilters = {};
    if (existingSelection) {
      newFilters = {
        ...selectedFilters,
        [filterType]: selectedFilters[filterType].filter(i => i.id !== item.id),
      };
    } else {
      if (Array.isArray(item)) {
        newFilters = {
          ...selectedFilters,
          [filterType]: item,
        };
      } else {
        newFilters = {
          ...selectedFilters,
          [filterType]: selectedFilters?.[filterType] ? [...selectedFilters[filterType], item] : [item],
        };
      }
    }
    setSelectedFilters(newFilters);
  };

  function normalizeSort(filters) {
    if (Array.isArray(filters.sort) && filters.sort.length > 0) {
      filters.sort = [filters.sort[filters.sort.length - 1]];
    }
    return filters;
  }

  const handleApplyFilters = () => {
    const currentPath = router.asPath;
    const [pathname, queryString] = currentPath.split('?');
    const existingParams = new URLSearchParams(queryString || '');
    const normalizedFilters = normalizeSort(selectedFilters);
    const filtersAsString = getFiltersAsUrlParams(normalizedFilters, existingParams).trim();

    // Create final query object
    const finalParams = new URLSearchParams();

    // Preserve subCategory if it exists
    if (existingParams.has('subCategory')) {
      finalParams.set('subCategory', existingParams.get('subCategory'));
    }

    // Preserve sort if it exists
    if (existingParams.has('sort')) {
      finalParams.set('sort', existingParams.get('sort'));
    }

    // Merge in the filters
    if (filtersAsString) {
      const filtersParams = new URLSearchParams(filtersAsString);
      for (const [key, value] of filtersParams.entries()) {
        finalParams.set(key, value);
      }
    }

    const finalQueryString = finalParams.toString();
    const newUrl = finalQueryString ? `${pathname}?${finalQueryString}` : pathname;

    router.push(newUrl, undefined, { shallow: true });
  };

  // const handleApplyFilters = () => {
  //   const currentPath = router.asPath;
  //   const [pathname, queryString] = currentPath.split('?');
  //   const existingSearchParams = new URLSearchParams(queryString || '');

  //   const filtersAsIds = getFiltersAsUrlParams(selectedFilters, existingSearchParams);

  //   const newUrl = filtersAsIds ? `${pathname}?${filtersAsIds}` : pathname;

  //   router.push(newUrl, undefined, { shallow: true });
  // };

  // const handleApplyFilters = () => {
  //   const existingSearchParams = new URLSearchParams(
  //     router.asPath.slice(router.asPath.toString().indexOf('?'), router.asPath.toString().length)
  //   );
  //   const filtersAsIds = getFiltersAsUrlParams(selectedFilters, existingSearchParams);

  //   router.push(`${router.asPath.slice(0, router.asPath.toString().indexOf('?'))}?${filtersAsIds}`, undefined, {
  //     shallow: true,
  //   });
  // };

  const getSelectedOfferFilters = selectedFilters => {
    if (!selectedFilters?.offers?.length) return {};

    //If all options are selected we need to send a different value
    if (selectedFilters.offers.length === OFFERS_OPTIONS.length) {
      return { withAnyOffer: true };
    }

    let offerFilters = {};
    selectedFilters.offers.map(i => {
      offerFilters[i.id] = true;
      return i;
    });
    return offerFilters;
  };

  const getSelectedFiltersAsIds = filters => {
    const filtersWithIds = {};

    const filtersWithoutOffers = { ...filters };
    delete filtersWithoutOffers.offers;
    for (let filter in filtersWithoutOffers) {
      if (filtersWithoutOffers[filter].length > 0) {
        filtersWithIds[filter] = filtersWithoutOffers[filter]?.map?.(i => decodeURIComponent(i?.id));
      }
    }
    return filtersWithIds;
  };

  const getFiltersAsUrlParams = (filters, existingUrlParams) => {
    const filtersWithIds = {};

    //category is a parameter used when in search results
    if (existingUrlParams.has('category')) {
      filtersWithIds.category = existingUrlParams.get('category');
    }
    //product is a parameter used when in search results
    if (existingUrlParams.has('product')) {
      filtersWithIds.product = existingUrlParams.get('product');
    }

    for (let i in filters) {
      if (i === 'offers') {
        filters.offers.map(o => {
          filtersWithIds[o.id] = true;
          return i;
        });
      } else if (filters[i].length > 0) {
        filtersWithIds[i] = filters[i]?.map(i => i?.id)?.toString();
      }
    }
    return decodeURIComponent(new URLSearchParams(filtersWithIds)?.toString());
  };

  const filtersWithIds = getSelectedFiltersAsIds(selectedFilters);
  const offerFilters = getSelectedOfferFilters(selectedFilters);

  const getNumberOfSelectedFilters = () => {
    let count = 0;
    for (let i in filtersWithIds) {
      count += filtersWithIds[i].length;
    }

    if (selectedFilters.offers) {
      count += selectedFilters.offers.length;
    }
    return count;
  };

  const clearFilters = (isOffers = false) => {
    setSelectedFilters(
      initialFilters || {
        tags: [],
        brands: [],
        attributes: [],
        categoryKinds: [],
      }
    );

    router.push(
      !isOffers ? router.asPath.split('?')[0] : router.asPath.split('?')[0] + `?withOffer=true&withBundleOffers=true`,
      undefined,
      {
        shallow: true,
      }
    );
  };

  const numberOfSelectedFilters = getNumberOfSelectedFilters();

  const transformUrlFiltersToState = () => {
    let newFilters = { tags: [], brands: [], attributes: [], categoryKinds: [], offers: [] };
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('tags')) {
      const tagIds = searchParams.get('tags').split(',');
      if (tags.length > 0) {
        newFilters.tags = tagIds.map(i => {
          return tags?.find?.(tag => tag.id === i);
        });
      } else {
        newFilters.tags = tagIds.map(i => {
          return { name: i, id: i };
        });
      }
    }

    if (searchParams.has('attributes')) {
      const attrsIds = searchParams.get('attributes').split(',');
      if (attributes.length > 0) {
        newFilters.attributes = attrsIds.map(i => {
          return attributes?.find?.(attr => attr.id === i);
        });
      } else {
        newFilters.attributes = attrsIds.map(i => {
          return { name: i, id: i };
        });
      }
    }

    if (searchParams.has('categoryKinds')) {
      const categoryKindIds = searchParams.get('categoryKinds').split(',');
      if (categoryKinds.length > 0) {
        newFilters.categoryKinds = categoryKindIds.map(i => {
          return categoryKinds?.find?.(kind => {
            if (kind.name === i) {
              return i;
            }
          });
        });
      } else {
        newFilters.categoryKinds = categoryKindIds.map(i => {
          return { name: i, id: i };
        });
      }
    }

    if (searchParams.has('brands')) {
      const brandsIds = searchParams.get('brands').split(',');
      if (brands.length > 0) {
        newFilters.brands = brandsIds.map(i => {
          return brands?.find?.(brand => {
            if (brand.name === i) {
              return decodeURIComponent(i);
            }
          });
        });
      } else {
        newFilters.brands = brandsIds.map(i => {
          return { name: i, id: i };
        });
      }
    }

    if (searchParams.has('withOffer')) {
      newFilters.offers = [OFFERS_OPTIONS[0]];
    }

    if (searchParams.has('withBundleOffers')) {
      newFilters.offers = [...newFilters.offers, OFFERS_OPTIONS[1]];
    }
    setSelectedFilters(newFilters);
    return newFilters;
  };

  const getFiltersFromUrl = () => {
    let newFilters = { tags: [], brands: [], attributes: [], categoryKinds: [], offers: [] };
    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.has('tags')) {
      const tagIds = searchParams.get('tags').split(',');
      if (tags.length > 0) {
        newFilters.tags = tagIds.map(i => {
          return tags?.find?.(tag => tag.id === i);
        });
      } else {
        newFilters.tags = tagIds.map(i => {
          return { name: i, id: i };
        });
      }
    }

    if (searchParams.has('attributes')) {
      const attrsIds = searchParams.get('attributes').split(',');
      if (attributes.length > 0) {
        newFilters.attributes = attrsIds.map(i => {
          return attributes?.find?.(attr => attr.id === i);
        });
      } else {
        newFilters.attributes = attrsIds.map(i => {
          return { name: i, id: i };
        });
      }
    }

    if (searchParams.has('categoryKinds')) {
      const categoryKindIds = searchParams.get('categoryKinds').split(',');
      if (categoryKinds.length > 0) {
        newFilters.categoryKinds = categoryKindIds.map(i => {
          return categoryKinds?.find?.(kind => {
            if (kind.name === i) {
              return i;
            }
          });
        });
      } else {
        newFilters.categoryKinds = categoryKindIds.map(i => {
          return { name: i, id: i };
        });
      }
    }

    if (searchParams.has('brands')) {
      const brandsIds = searchParams.get('brands').split(',');
      if (brands.length > 0) {
        newFilters.brands = brandsIds.map(i => {
          return brands?.find?.(brand => {
            if (brand.name === i) {
              return decodeURIComponent(i);
            }
          });
        });
      } else {
        newFilters.brands = brandsIds.map(i => {
          return { name: i, id: i };
        });
      }
    }
    if (searchParams.has('withBundleOffers')) {
      newFilters.offers = [OFFERS_OPTIONS[1]];
    }
    if (searchParams.has('withOffer')) {
      newFilters.offers = [...newFilters.offers, OFFERS_OPTIONS[0]];
    }
    const filtersWithIds = getSelectedFiltersAsIds(newFilters);
    const offerFilters = getSelectedOfferFilters(newFilters);
    setSelectedFilters(newFilters);
    return { filtersWithIds, offerFilters };
  };

  useEffect(() => {
    setIsFirstTimeMounting(true);
  }, []);

  useEffect(() => {
    //clear selectedFilters on category change / on router path change it will unmount anyway
    if (!isFirstTimeMounting) clearFilters();
  }, [categoryId]);

  useEffect(() => {
    transformUrlFiltersToState();
  }, [tags.length, attributes.length, brands.length, categoryKinds.length, JSON.stringify(initialFilters)]);

  return {
    tags,
    brands,
    attributes,
    categoryKinds,
    isLoading,
    getCategoryFilters,
    selectedFilters,
    setSelectedFilters,
    filtersWithIds,
    offerFilters,
    numberOfSelectedFilters,
    handleSelectFilter,
    clearFilters,
    handleApplyFilters,
    transformUrlFiltersToState,
    getFiltersFromUrl,
  };
}
